import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Modal, DatePicker, Button } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import "dayjs/locale/en";

import { ordersService } from "../../../../services/orders-services";
import { ordersActions } from "../../../../store/slices/orders-slices";

const OrdersMoveDeliveryToAnotherDateModal = ({
  setIsShowMoveDelivery,
  date,
  order,
  setIsShowCancellation,
}) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(date ? new Date(date) : null);
  const [isLoading, setIsLoading] = useState(false);

  const [history, setHistory] = useState({});

  const localeMap = {
    en: "en",
    "en-EN": "en",
    ru: "ru",
    "ru-RU": "ru",
  };

  const currentLanguage = i18n.language;
  const currentLocale = localeMap[currentLanguage] || "en";

  // Set dayjs locale
  dayjs.locale(currentLocale);

  const handleCancel = () => setIsShowMoveDelivery(false);

  useEffect(() => {
    const fetchOrderHistory = async () => {
      try {
        const response = await ordersService.getHistoryOrderById(order.id);

        const disabledDates =
          response?.excludedDates?.map((dateStr) => new Date(dateStr)) || [];

        setHistory({
          ...response,
          excludedDates: disabledDates,
        });
      } catch (error) {
        console.error(t("modal-toast-creating-error"), error);
        toast.error(t("modal-toast-creating-error"));
      }
    };

    fetchOrderHistory();
  }, [order.id]);

  const handleConfirm = async () => {
    if (isLoading) return;
    
    if (!selectedDate) {
      toast.error(t("Please select a date"));
      return;
    }
    
    setIsLoading(true);
    try {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const currentData = `${year}-${month}-${day}`;

      const formatedData = {
        orderId: Number(order.id),
        packId: order.packId,
        date: date,
        newDate: currentData,
      };

      const result = await ordersService.moveToAnotherDate(formatedData);

      if (result?.menuFound === false) {
        toast.warning(t("No menu configured for the target date — existing dishes were kept"));
      } else {
        toast.success(t("Delivery moved successfully"));
      }
      setIsShowCancellation(true);
      setIsShowMoveDelivery(false);
      dispatch(ordersActions.changeTrigger());
    } catch (error) {
      console.error(t("modal-toast-creating-error"), error);
      toast.error(
        error?.response?.data?.message || t("modal-toast-creating-error")
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={t("move-delivery")}
      open={true}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t("modal-cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          onClick={handleConfirm}
        >
          {t("Ok")}
        </Button>,
      ]}
      centered
      width={500}
    >
      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <DatePicker
          size="large"
          value={selectedDate ? dayjs(selectedDate) : null}
          onChange={(date) => setSelectedDate(date ? date.toDate() : null)}
          placeholder={t("select-date")}
          format="DD.MM.YYYY"
          allowClear
          disabledDate={(current) => {
            if (
              !history?.excludedDates ||
              history.excludedDates.length === 0
            ) {
              return false;
            }
            return history.excludedDates.some((excludedDate) => {
              return current && current.isSame(dayjs(excludedDate), "day");
            });
          }}
          cellRender={(current, info) => {
            if (info.type !== 'date') return info.originNode;
            
            const isExcluded = history?.excludedDates?.some((excludedDate) => {
              return current && current.isSame(dayjs(excludedDate), "day");
            });

            if (isExcluded) {
              return (
                <div
                  className="ant-picker-cell-inner"
                  style={{
                    border: "1px solid red",
                    borderRadius: "5px",
                  }}
                >
                  {current.date()}
                </div>
              );
            }

            return info.originNode;
          }}
          style={{ width: "100%" }}
        />
      </div>
    </Modal>
  );
};

export { OrdersMoveDeliveryToAnotherDateModal };
